import {
  type ChatMessageDto,
  fetchDayOfEmergencyAnonymousAccessListRecipientEmergencyChatMessages,
} from "@crowdcoursing/api/openapi";
import { HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useBackendUrl } from "~services/providers/BackendUrlProvider";
import { withRetries } from "~utils/url";

import { type ChatMember, type ChatMessage } from "./useChat";
import { useFeature } from "./useFeature";

/** Must match API `MapHub<RecipientEmergencyChatHub>("/hubs/recipient-emergency-chat")`. */
const hubRelativePath = "hubs/recipient-emergency-chat";

function buildRecipientEmergencyHubUrl(baseUrl: string): string {
  const trimmed = baseUrl.trim().replace(/\/+$/, "");
  return `${trimmed}/${hubRelativePath.replace(/^\/+/, "")}`;
}

/** Same payload shape as staff pod chat (`ChatMessageDto` / `IChatClient`). */
function mapServerMessage(raw: Record<string, unknown>): ChatMessage | null {
  const id = raw.id != null ? String(raw.id) : "";
  const body = typeof raw.body === "string" ? raw.body : "";
  const userName = typeof raw.userName === "string" ? raw.userName : "";
  const userId =
    raw.lawEnforcementUserId != null ? String(raw.lawEnforcementUserId) : raw.userId != null ? String(raw.userId) : "";
  if (!id || !userId) {
    return null;
  }
  const ts = raw.timestamp;
  const timestamp =
    typeof ts === "string"
      ? new Date(ts)
      : ts instanceof Date
        ? ts
        : typeof ts === "object" && ts != null && "toString" in ts
          ? new Date(String(ts))
          : new Date();
  return { body, id, timestamp, userId, userName: userName || "User" };
}

function mapDtoToChatMessage(dto: ChatMessageDto): ChatMessage | null {
  const id = dto.id?.trim() ?? "";
  const userId = dto.userId?.trim() ?? "";
  if (!id || !userId) {
    return null;
  }
  const body = dto.body ?? "";
  const userName = dto.userName?.trim() || "User";
  const timestamp = dto.timestamp ? new Date(dto.timestamp) : new Date();
  return { body, id, timestamp, userId, userName };
}

function mapPresence(raw: Record<string, unknown>): ChatMember | null {
  const userId =
    raw.lawEnforcementUserId != null ? String(raw.lawEnforcementUserId) : raw.userId != null ? String(raw.userId) : "";
  const userName = typeof raw.userName === "string" ? raw.userName : "";
  if (!userId) {
    return null;
  }
  return { userId, userName: userName || "User" };
}

const createConnection = (hubUrl: string, enableLogs: boolean) =>
  new HubConnectionBuilder()
    .withUrl(hubUrl, {
      logger: enableLogs ? LogLevel.Debug : LogLevel.None,
    })
    .withAutomaticReconnect()
    .build();

export interface UseRecipientEmergencyChatArgs {
  dayOfEmergencyId: string;
  accessToken: string;
  enabled?: boolean;
}

export interface RecipientEmergencyChatRoom {
  messages: ChatMessage[];
  members: ChatMember[];
  isConnected: boolean;
  connectionError: string | null;
  addMessage: (body: string) => Promise<unknown>;
}

/**
 * Shared reunification emergency day chat (same `ChatMessage` stream as signed-in staff pod chat), via recipient magic-link.
 * Hub: `/hubs/recipient-emergency-chat` — Join(dayOfEmergencyId, accessToken), Send(body), Leave().
 */
export const useRecipientEmergencyChat = ({
  dayOfEmergencyId,
  accessToken,
  enabled = true,
}: UseRecipientEmergencyChatArgs): RecipientEmergencyChatRoom => {
  const { backendUrl } = useBackendUrl();
  const enableLogs = useFeature("chatLogs");
  const hubUrl = useMemo(
    () => (backendUrl && enabled && dayOfEmergencyId && accessToken ? buildRecipientEmergencyHubUrl(backendUrl) : null),
    [accessToken, backendUrl, dayOfEmergencyId, enabled],
  );

  const connection = useMemo(() => {
    if (!hubUrl) {
      return null;
    }
    return createConnection(hubUrl, enableLogs);
  }, [enableLogs, hubUrl]);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [members, setMembers] = useState(new Map<string, ChatMember>());
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    if (!connection || !dayOfEmergencyId || !accessToken || !backendUrl) {
      return;
    }

    const ac = new AbortController();
    let cancelled = false;

    const onMessage = (msg: unknown) => {
      const raw = msg as Record<string, unknown>;
      const mapped = mapServerMessage(raw);
      if (!mapped) {
        return;
      }
      setMessages((prev) => (prev.some((m) => m.id === mapped.id) ? prev : [mapped, ...prev]));
    };

    const onUserJoined = (member: unknown) => {
      const m = mapPresence(member as Record<string, unknown>);
      if (!m) {
        return;
      }
      setMembers((prev) => new Map(prev.set(m.userId, m)));
    };

    const onUserLeft = (member: unknown) => {
      const m = mapPresence(member as Record<string, unknown>);
      if (!m) {
        return;
      }
      setMembers((prev) => {
        const next = new Map(prev);
        next.delete(m.userId);
        return next;
      });
    };

    connection.on("MessageReceived", onMessage);
    connection.on("UserJoined", onUserJoined);
    connection.on("UserLeft", onUserLeft);

    connection.onreconnecting(() => {
      setIsConnected(false);
      setConnectionError(null);
      setMessages([]);
      setMembers(new Map());
    });

    connection.onreconnected(async () => {
      try {
        const joined = await withRetries(() => connection.invoke("Join", dayOfEmergencyId, accessToken));
        if (!joined) {
          throw new Error("Could not rejoin emergency chat.");
        }
        setIsConnected(true);
        setConnectionError(null);
      } catch (e) {
        console.error("Recipient emergency chat re-join failed", e);
        setConnectionError(e instanceof Error ? e.message : "Could not rejoin chat.");
      }
    });

    const loadHistory = async () => {
      const dtos = await fetchDayOfEmergencyAnonymousAccessListRecipientEmergencyChatMessages(
        {
          baseUrlOverride: backendUrl,
          headers: { "X-Emergency-Access-Token": accessToken },
          pathParams: { dayOfEmergencyId },
        },
        ac.signal,
      );
      if (cancelled) {
        return;
      }
      const mapped = dtos.map((d) => mapDtoToChatMessage(d)).filter((m): m is ChatMessage => m != null);
      setMessages([...mapped].reverse());
    };

    const startAndJoin = async () => {
      const started = await withRetries(() => connection.start());
      if (!started) {
        throw new Error("Could not connect to emergency chat.");
      }
      if (connection.state !== HubConnectionState.Connected) {
        throw new Error("Could not connect to emergency chat.");
      }
      const joined = await withRetries(() => connection.invoke("Join", dayOfEmergencyId, accessToken));
      if (!joined) {
        throw new Error("Could not join emergency chat.");
      }
      setIsConnected(true);
      setConnectionError(null);
    };

    void (async () => {
      try {
        try {
          await loadHistory();
        } catch (e) {
          if ((e as Error)?.name === "AbortError") {
            return;
          }
          console.warn("Recipient emergency chat history could not be loaded", e);
        }
        if (cancelled) {
          return;
        }
        await startAndJoin();
      } catch (e) {
        if (!cancelled) {
          setIsConnected(false);
          setConnectionError(e instanceof Error ? e.message : "Could not connect to emergency chat.");
        }
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
      setIsConnected(false);
      setMessages([]);
      setMembers(new Map());
      connection.off("MessageReceived", onMessage);
      connection.off("UserJoined", onUserJoined);
      connection.off("UserLeft", onUserLeft);
      void (async () => {
        try {
          if (connection.state === HubConnectionState.Connected) {
            await connection.invoke("Leave");
          }
        } catch {
          /* ignore */
        }
        await connection.stop();
      })();
    };
  }, [backendUrl, connection, dayOfEmergencyId, accessToken]);

  const addMessage = useCallback(
    (body: string) => {
      if (!connection) {
        return Promise.reject(new Error("not ready yet"));
      }
      if (connection.state !== HubConnectionState.Connected) {
        return Promise.reject(new Error("Chat is not connected."));
      }
      return withRetries(() => connection.invoke("Send", body)).then((ok) => {
        if (!ok) {
          throw new Error("Could not send message.");
        }
      });
    },
    [connection],
  );

  return {
    addMessage,
    connectionError,
    isConnected,
    members: [...members.values()],
    messages,
  };
};
